import { BigNum, BN, SwapQuote } from '@velocity-exchange/sdk';

/**
 * The amount the pre-signed deposit tx commits to. The swap can never deliver
 * less than this without failing, so the deposit can never overdraw; any
 * positive difference stays in the wallet as dust.
 *
 * `otherAmountThreshold` is the floor the swap actually enforces on-chain —
 * net of slippage *and* any platform fee — so prefer it. Only Jupiter sets it;
 * derive the floor from slippage alone otherwise, accepting that a platform
 * fee would make that derivation too generous.
 */
export const getMinOutBigNum = (
	quote: Pick<SwapQuote, 'outAmount' | 'slippageBps' | 'otherAmountThreshold'>,
	outPrecisionExp: BN
): BigNum => {
	if (quote.otherAmountThreshold) {
		return BigNum.from(new BN(quote.otherAmountThreshold), outPrecisionExp);
	}

	const minOutRaw = new BN(quote.outAmount)
		.mul(new BN(10_000 - quote.slippageBps))
		.div(new BN(10_000));
	return BigNum.from(minOutRaw, outPrecisionExp);
};

/**
 * Whether `quote` still describes `amount`. Quoting is debounced, so right
 * after an edit the held quote is the previous amount's quote with nothing
 * else marking it stale — displaying or swapping it would use the wrong
 * amount.
 */
export const isQuoteForAmount = (
	quote: Pick<SwapQuote, 'inAmount'> | null | undefined,
	amount: BigNum
): boolean => {
	if (!quote) return false;

	try {
		return new BN(quote.inAmount).eq(amount.val);
	} catch {
		return false;
	}
};

interface TokenBalanceLike {
	owner?: string;
	mint: string;
	uiTokenAmount: { amount: string };
}

/**
 * Exact amount of `mint` credited to `owner` by a confirmed transaction,
 * read from its pre/post token-balance meta. Null when the tx meta doesn't
 * show a post-balance for the pair (caller should fall back to minOut).
 */
export const getReceivedTokenAmount = ({
	meta,
	mint,
	owner,
	precisionExp,
}: {
	meta: {
		preTokenBalances?: TokenBalanceLike[] | null;
		postTokenBalances?: TokenBalanceLike[] | null;
	} | null;
	mint: string;
	owner: string;
	precisionExp: BN;
}): BigNum | null => {
	if (!meta) return null;

	const matches = (b: TokenBalanceLike) => b.owner === owner && b.mint === mint;

	const post = meta.postTokenBalances?.find(matches);
	if (!post) return null;

	const pre = meta.preTokenBalances?.find(matches);

	const postAmount = BigNum.from(post.uiTokenAmount.amount, precisionExp);
	const preAmount = pre
		? BigNum.from(pre.uiTokenAmount.amount, precisionExp)
		: BigNum.zero(precisionExp);

	const received = postAmount.sub(preAmount);
	return received.gtZero() ? received : null;
};
